import React, { useState } from 'react'
import Linkify from 'react-linkify'

import { Maybe, TowingRecordRouteType, TypeIdentifier } from '../../../../common/types'
import { FlexContainer, Section, SectionTextColumn, Text } from '../../../../components/layout'
import { SectionBody, SectionHeader } from '../../../../components/layout/section'
import LinkButton from '../../../../components/LinkButton'
import { parseAddress, parseLocationQueryString } from '../../../../components/Parsers'
import { PhoneNumber } from '../../../../components/PhoneNumber'
import { Styling } from '../../../../components/Styling'
import { BuildJobDetails } from '../../../../components/TowingCommon/JobDetails'
import {
  transformDrive,
  transformFuel,
  transformTransmission,
  transformVehicleClass,
} from '../../../../components/VehicleSearchModal/transformers'
import { VehicleInfo } from '../../../../components/VehicleSearchModal/types'
import { toMultilineText } from '../../../../util/common'
import { getIndexOfRoute } from '../routes/getIndexOfRoute'
import { SummaryTowingRecord } from './types'
import { getRepairShop } from './getRepairShop'
import { MapLink } from '../../../../components/MapLink'
import { Images } from '../../../../components/images'
import { ClipboardButton } from '../../../../components/ClipboardButton'
import { VehicleInfoDataSource } from '../../../../components/VehicleDetails/types'
import { TraficomInfoText } from '../../../../components/VehicleDetails/TraficomInfoText'
import { useRepairShops } from '../routes/subforms/FinishRoute/options'

interface Props {
  towingRecord: SummaryTowingRecord
  onVehicleInfoLinkClick: () => void
  vehicleInfoCheck: Maybe<VehicleInfo>
  refetchData?: () => void
  imagesCount: number
}

export const SummaryBody: React.FC<Props> = ({ towingRecord, onVehicleInfoLinkClick, vehicleInfoCheck, refetchData, imagesCount }) => {
  const [showImageModal, setShowImageModal] = useState<boolean>(false)
  const fromRoute = getIndexOfRoute(towingRecord.routes, TowingRecordRouteType.arrival)
  const toRoute = getIndexOfRoute(towingRecord.routes, TowingRecordRouteType.finish)
  const { repairShops } = useRepairShops({ skip: towingRecord.typeIdentifier !== TypeIdentifier.autoliitto})
  const repairShop = getRepairShop(repairShops, towingRecord)

  const isTrafiVehicleData = vehicleInfoCheck && vehicleInfoCheck.searchMeta && vehicleInfoCheck.searchMeta.dataSource === VehicleInfoDataSource.traficom ? true : false

  const toggleImageModal = () => {
    if(showImageModal && refetchData) refetchData()
    setShowImageModal(!showImageModal)
  }

  return (
    <Styling padding="0 2rem" paddingMobile="0 0.5rem">
      <Section gap="2rem" justify="text" columns={2}>
        <SectionTextColumn width="100%">
          <SectionHeader>
            <Text size="medium">{`Ajoneuvotiedot`}</Text>
            <LinkButton onClick={onVehicleInfoLinkClick}>ATJ-tiedot</LinkButton>
          </SectionHeader>
          <SectionBody>
            {vehicleInfoCheck ? (
              <>
                <Text size="medium">{transformVehicleClass(vehicleInfoCheck.vehicle.vehicleClass)}</Text>
                <Text size="medium">
                  {`${transformTransmission(vehicleInfoCheck.vehicle.transmission)} / ${transformDrive(
                    vehicleInfoCheck.vehicle.driveTrain
                  )} / ${transformFuel(vehicleInfoCheck.vehicle.fuel)}`}
                </Text>
                <TraficomInfoText show={isTrafiVehicleData} />
              </>
            ) : (
              <Text size="medium">{towingRecord.vehicleDetails.vehicleClass}</Text>
            )}
          </SectionBody>
        </SectionTextColumn>
        <SectionTextColumn width="100%">
          <SectionHeader>
            <Text size="medium">{`Tilaaja`}</Text>
          </SectionHeader>
          <SectionBody>
            {towingRecord.orderer && towingRecord.orderer.__typename === 'PersonCustomer' && (
              <Text size="medium">{`${towingRecord.orderer.firstName} ${towingRecord.orderer.lastName}`}</Text>
            )}
            {towingRecord.orderer && towingRecord.orderer.__typename === 'CompanyCustomer' && (
              <Text size="medium">{`${towingRecord.orderer.companyName}`}</Text>
            )}
            {towingRecord.orderer && towingRecord.orderer.phone ? (
                <PhoneNumber size="medium" phone={towingRecord.orderer.phone} />
              ) : (
                <Text style={{ visibility: "hidden" }} size="medium">-</Text> 
              )
            }
          </SectionBody>
        </SectionTextColumn>
        <SectionTextColumn width="100%">
          <SectionHeader>
            <Text size="medium">Mistä</Text>
          </SectionHeader>
          <SectionBody>
            {fromRoute > -1 ? (
              <>
                <FlexContainer style={{gap: "10px"}}>
                  <MapLink
                    size="medium"
                    queryString={parseLocationQueryString(towingRecord.routes[fromRoute])}
                    address={parseAddress(towingRecord.routes[fromRoute].address, towingRecord.routes[fromRoute].city)}
                  />
                   <ClipboardButton
                    copy={
                      towingRecord.routes[fromRoute]?.coords?.lat && towingRecord.routes[fromRoute]?.coords?.long
                        ? `${towingRecord.routes[fromRoute].coords.lat}, ${towingRecord.routes[fromRoute].coords.long}`
                        : parseAddress(towingRecord.routes[fromRoute].address, towingRecord.routes[fromRoute].city)
                    }
                  />
                </FlexContainer>
                <Text overflow='break-word' size="medium">{`${towingRecord.routes[fromRoute].description}`}</Text>
              </>
            ) : (
              <Text>-</Text>
            )}
          </SectionBody>
        </SectionTextColumn>
        <SectionTextColumn width="100%">
          <SectionHeader>
            <Text size="medium">Mihin</Text>
          </SectionHeader>
          <SectionBody>
            {toRoute > -1 && towingRecord.routes[toRoute].address ? (
              <>
              <FlexContainer style={{gap: "10px"}}>
                  <MapLink
                    size="medium"
                    queryString={parseLocationQueryString(towingRecord.routes[toRoute])}
                    address={parseAddress(towingRecord.routes[toRoute].address, towingRecord.routes[toRoute].city)}
                  />
                  <ClipboardButton
                    copy={
                      towingRecord.routes[toRoute]?.coords?.lat && towingRecord.routes[toRoute]?.coords?.long
                        ? `${towingRecord.routes[toRoute].coords.lat}, ${towingRecord.routes[toRoute].coords.long}`
                        : parseAddress(towingRecord.routes[toRoute].address, towingRecord.routes[toRoute].city)
                    }
                  />
                </FlexContainer>
                <Text overflow='break-word' size="medium">{`${towingRecord.routes[toRoute].description}`}</Text>
              </>
            ) : repairShop &&
              towingRecord.typeIdentifier === TypeIdentifier.autoliitto &&
              towingRecord.jobDetails.serviceType !== 'towing' ? (
              <>
                <Text size="medium" fontWeight="bold">
                  Jos työ muuttuu hinaukseksi:
                </Text>
                <Text size="medium">{parseAddress(repairShop.location.address, repairShop.location.city)}</Text>
                <Text size="medium">{repairShop.name}</Text>
              </>
            ) : (
               <Text size="medium">-</Text>
            )}
          </SectionBody>
        </SectionTextColumn>
        <BuildJobDetails towingObject={towingRecord} />
        {towingRecord.typeIdentifier !== TypeIdentifier.sos && (
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">Lisäselvitykset</Text>
            </SectionHeader>
            <SectionBody>
              <Linkify>{toMultilineText(towingRecord.jobDetails.additionalInfo)}</Linkify>
            </SectionBody>
          </SectionTextColumn>
        )}
        <SectionTextColumn width="100%">
          <SectionHeader>
            <Text size="medium">Muistiinpanot</Text>
          </SectionHeader>
          <SectionBody>
            <Linkify>{toMultilineText(towingRecord.jobDetails.notes)}</Linkify>
          </SectionBody>
        </SectionTextColumn>
      </Section>
      {showImageModal && (
        <Images
          onClose={toggleImageModal}
          towingRecordId={towingRecord.id}
          vehicleRegNo={towingRecord.vehicleDetails.makeAndModel}
        />
      )}
      <SectionTextColumn width="100%">
        <SectionHeader>
          <Text size="medium">Kuvat</Text>
        </SectionHeader>
        <SectionBody>
          <Linkify>
            <LinkButton onClick={toggleImageModal}>
              {imagesCount > 0 ? `Lisättyjä kuvia (${imagesCount})` : `Ei lisättyjä kuvia`}
            </LinkButton>
          </Linkify>
        </SectionBody>
      </SectionTextColumn>
    </Styling>
  )
}
